import React, { FC, useEffect, useState } from 'react';
import {
  Control,
  UseFormClearErrors,
  UseFormGetFieldState,
  UseFormResetField,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { HelpIconVariant } from '../../../../assets/icons/help-icon';
import { HelpIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Checkbox } from '../../../../components/checkbox/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { Stakeholder } from '../../../../types/stakeholder.types';
import { TableItem } from './form-components/index';
import {
  FormSchema,
  terminateForm as terminateFormSchema,
} from './terminate-stakeholder-modal.validation';

export type TerminateFormProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  editMode?: boolean;
  filedState: UseFormGetFieldState<FormSchema>;
  handleCloseModal: () => void;
  handleTerminate: () => void;
  resetField: UseFormResetField<FormSchema>;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  stakeholders?: Stakeholder[];
};

export const TerminateForm: FC<TerminateFormProps> = ({
  control,
  setFormData,
  companyId,
  filedState,
  handleCloseModal,
  handleTerminate,
  stakeholders,
  editMode,
  clearErrors,
  setError,
}) => {
  const [selectedId, setSelectedId] = useState<string>('');
  const { terminateForm } = useWatch<FormSchema>({ control });
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const success = terminateFormSchema.safeParse(terminateForm).success;
    const fieldState = filedState('terminateForm');

    setIsValid(success && !fieldState.invalid);
  }, [terminateForm?.stakeholderItems, terminateForm?.isAgree, terminateForm, filedState]);

  useEffect(() => {
    if (
      !terminateForm?.stakeholderItems?.every(
        ({ grantItems }) => !grantItems || !grantItems.length,
      ) ||
      terminateForm?.isAgree
    )
      return;
    setFormData('terminateForm.isAgree', true);
  }, [setFormData, terminateForm?.isAgree, terminateForm?.stakeholderItems]);

  if (!stakeholders) return;

  return (
    <>
      <div className="flex w-full flex-col rounded-lg border-[1px] border-gray-100 bg-white">
        <table className="h-fit w-full transition-all">
          <thead>
            <tr className="flex w-full bg-gray-50">
              <td className="p-0">
                <div className="h-full w-[48px]" />
              </td>
              <td className="w-[195px] items-center px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="STAKEHOLDER" />
                </span>
              </td>
              <td className="w-[129px] items-center px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="CURRENT_TYPE" />
                </span>
              </td>
              <td className="flex w-[129px] items-center gap-2 px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="NEW_TYPE" />
                </span>
                <HoverCard>
                  <HoverCardTrigger>
                    <HelpIcon variant={HelpIconVariant.EXERCISE} />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="bottom"
                    triangleClassName="top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      This defines the stakeholder’s new type after leaving — like Ex-Employee or
                      Ex-Advisor.
                    </span>
                  </HoverCardContent>
                </HoverCard>
              </td>
              <td className="flex w-[184px] items-center gap-2 px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="TERMINATION_TYPE" />
                </span>
                <HoverCard>
                  <HoverCardTrigger>
                    <HelpIcon variant={HelpIconVariant.EXERCISE} />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="bottom"
                    triangleClassName="top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      Selecting a termination reason may impact the post-termination exercise period
                      for granted shares.
                    </span>
                  </HoverCardContent>
                </HoverCard>
              </td>
              <td className="flex w-[184px] items-center gap-2 px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="TERMINATION_DATE" />
                </span>
                <HoverCard>
                  <HoverCardTrigger>
                    <HelpIcon variant={HelpIconVariant.EXERCISE} />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="bottom"
                    triangleClassName="top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      The official date when the stakeholder's status changed to terminated.
                    </span>
                  </HoverCardContent>
                </HoverCard>
              </td>
              <td className="flex w-[184px] items-center gap-2 px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="LAST_VESTING_DATE" />
                </span>
                <HoverCard>
                  <HoverCardTrigger>
                    <HelpIcon variant={HelpIconVariant.EXERCISE} />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="bottom"
                    triangleClassName="top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      The final date on which vesting will occur for the stakeholder.
                    </span>
                  </HoverCardContent>
                </HoverCard>
              </td>
              <td className="flex w-[190px] items-center px-4 py-3">
                <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id="CONTACT_INFO" />
                </span>
              </td>
            </tr>
          </thead>
          <tbody className="divide-y-[1px] divide-gray-100">
            {stakeholders.map((stakeholder, index) => (
              <TableItem
                clearErrors={clearErrors}
                companyId={companyId}
                control={control}
                editMode={editMode}
                key={stakeholder.id}
                selectedId={selectedId}
                setError={setError}
                setFormData={setFormData}
                setSelectedId={setSelectedId}
                stakeholder={stakeholder}
                stakeholderIndex={index}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex h-[84px] w-full items-end justify-between gap-3 bg-white pt-10">
        <div className="flex h-11 items-center gap-8">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={terminateForm?.isAgree}
              onChange={(e) => {
                setFormData('terminateForm.isAgree', e);
              }}
            />
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="CONFIRM_DAYS_CORRECT" />
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={terminateForm?.isSendEmails}
              onChange={(e) => {
                setFormData('terminateForm.isSendEmails', e);
              }}
            />
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SEND_EMAIL_TERMINATION" />
            </span>
          </div>
        </div>

        <div className="flex h-11 gap-4">
          <Button
            className="w-fit bg-transparent p-3 text-sm font-[450] text-gray-700"
            onClick={handleCloseModal}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          {editMode ? (
            <Button
              className="h-full w-[260px] bg-brand-700 p-3 text-sm font-[550] text-gray-25"
              disabled={!isValid}
              onClick={handleTerminate}
              type="button"
            >
              <AppFormattedMessage id="UPDATE_TERMINATION" />
            </Button>
          ) : (
            <Button
              className="h-full w-[260px] bg-fireside-600 p-3 text-sm font-[550] text-gray-25"
              disabled={!isValid}
              onClick={handleTerminate}
              type="button"
            >
              <AppFormattedMessage id="TERMINATE" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
